import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DiscountType, type PromoCodeData } from "@/core/entities/promo-code";
import { ProductType } from "@/core/entities/product";
import { UseFormReturn } from "react-hook-form";
import { useState, useEffect } from "react";

interface PromoCodeFormProps {
  form: UseFormReturn<PromoCodeData>;
  onSubmit: () => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  submitText: string;
}

export const PromoCodeForm = ({
  form,
  onSubmit,
  onCancel,
  isLoading,
  submitText,
}: PromoCodeFormProps) => {
  const discountType = form.watch("discount.type");
  const discountValue = form.watch("discount.value");
  const [discountValueDisplay, setDiscountValueDisplay] = useState<string>(
    discountValue === 0 ? "" : String(discountValue ?? "")
  );

  // Sync display value when form value changes externally (e.g., form reset)
  useEffect(() => {
    if (discountValue === 0) {
      setDiscountValueDisplay("");
    } else if (discountValue !== undefined && discountValue !== null) {
      setDiscountValueDisplay(String(discountValue));
    }
  }, [discountValue]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Summer Sale 2024" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Code</FormLabel>
              <FormControl>
                <Input
                  placeholder="SUMMER2024"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value.toUpperCase());
                  }}
                  className="font-mono"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="discount.type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={DiscountType.PERCENTAGE}>
                      Percentage
                    </SelectItem>
                    <SelectItem value={DiscountType.FIXED}>Fixed</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="discount.value"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Discount Value{" "}
                  {discountType === DiscountType.PERCENTAGE ? "(%)" : "($)"}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={
                      discountType === DiscountType.PERCENTAGE ? "10" : "50"
                    }
                    value={discountValueDisplay}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Update display value immediately to allow deletion
                      setDiscountValueDisplay(value);
                      
                      // Allow empty value while typing - don't convert to 0 immediately
                      if (value === "" || value === null) {
                        // Don't update form field when empty, allows user to clear and type easily
                        return;
                      }
                      const numValue = parseFloat(value);
                      // Only update form field if it's a valid number
                      if (!isNaN(numValue)) {
                        field.onChange(numValue);
                      }
                    }}
                    onBlur={(e) => {
                      // On blur, if empty, convert to 0 (required by schema)
                      const value = e.target.value;
                      if (value === "" || value === null || isNaN(parseFloat(value))) {
                        setDiscountValueDisplay("");
                        field.onChange(0);
                      }
                      field.onBlur();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="enabledFor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Enabled For</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={ProductType.BUY}>Buy</SelectItem>
                  <SelectItem value={ProductType.SELL}>Sell</SelectItem>
                  <SelectItem value={ProductType.BOTH}>Both</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expirationDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expiration Date (Optional)</FormLabel>
              <FormControl>
                <Input
                  type="datetime-local"
                  {...field}
                  value={
                    field.value
                      ? new Date(field.value).toISOString().slice(0, 16)
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value ? new Date(value) : undefined);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-4 pt-4">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : submitText}
          </Button>
        </div>
      </form>
    </Form>
  );
};
